import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE, TypographySize } from 'mm-theme-configuration/dist/consts';
import { TextLinkComponent, ThemedLinkStyle, generateLinkStyleFromTheme } from '../../partials/link/TextLink';
import { useTheme } from '../../../theming/useTheme';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { H4, P, semanticCSS } from '../../../typography/semanticTags';
import { baseUnit } from '../../../theming/baseUnitDefinition';
import { getLayoutComponentPadding, LayoutComponentBaseProps } from '../../components/pageLayouts/layout.utils';
import { SeeAllArrowButton } from './SeeAllArrowButton';

export const componentConfigurationKey = 'sectionHeader';

const createStyles = ({
  titleColor,
  titleBackgroundColor,
  borderTopColor,
  squareColor,
  showBorderTop,
  showBorderBottom,
  borderTopWidth,
  showStrikeThrowHorizontalLine,
  strikeThrowHorizontalLineColor,
  middleHorizontalLineColor,
  largeVerticalGap,
  mediumVerticalGap,
  smallVerticalGap,
  largeHorizontalGap,
  mediumHorizontalGap,
  smallHorizontalGap,
  largeWrapperGap,
  mediumWrapperGap,
  smallWrapperGap,
  descriptionColor,
  borderBottomColor,
  borderBottomWidth }: SectionHeaderComponentThemeProps) => {
  return StyleSheet.create({
    container: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      marginBottom: `${baseUnit * 0.5}px`,
      flexDirection: 'column',
    },
    titleItem: {
      flex: '1 1 auto',
      color: titleColor,
      display: 'inline-flex',
      alignItems: 'center',
      width: '100%',
      position: 'relative',
      zIndex: 0,
      borderTop: showBorderTop ? `${baseUnit * borderTopWidth}px solid ${borderTopColor}` : 'unset',
      borderBottom: showBorderBottom ? `${baseUnit * borderBottomWidth}px solid ${borderBottomColor}` : 'unset',
      backgroundColor: titleBackgroundColor,
      [MEDIA_BREAKPOINTS.large]: {
        padding: `${largeHorizontalGap * baseUnit}px ${largeWrapperGap * baseUnit}px ${largeHorizontalGap * baseUnit}px ${largeWrapperGap * baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        padding: `${mediumHorizontalGap * baseUnit}px ${mediumWrapperGap * baseUnit}px ${mediumHorizontalGap * baseUnit}px ${mediumWrapperGap * baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        padding: `${smallHorizontalGap * baseUnit}px ${smallWrapperGap * baseUnit}px ${smallHorizontalGap * baseUnit}px ${smallWrapperGap * baseUnit}px`,
      },
      ':before': {
        ...showStrikeThrowHorizontalLine ? {
          content: '""',
          right: 0,
          left: 0,
          position: 'absolute',
          zIndex: -1,
          width: '100%',
          [MEDIA_BREAKPOINTS.large]: {
            borderTop: `${0.75 * baseUnit}px solid ${strikeThrowHorizontalLineColor}`,
          },
          [MEDIA_BREAKPOINTS.medium]: {
            borderTop: `${0.5 * baseUnit}px solid ${strikeThrowHorizontalLineColor}`,
          },
          [MEDIA_BREAKPOINTS.small]: {
            borderTop: `${0.5 * baseUnit}px solid ${strikeThrowHorizontalLineColor}`,
          },
        } : null,
      },
    },
    square: {
      backgroundColor: squareColor,
      marginRight: `${baseUnit * 0.5}px`,
      [MEDIA_BREAKPOINTS.large]: {
        width: '28px',
        height: '28px',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '24px',
        height: '24px',
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '20px',
        height: '20px',
      },
    },
    middleHorizontalLineContainer: {
      flexGrow: 1,
      ':before': {
        content: '""',
        display: 'block',
        borderTop: `2px solid ${middleHorizontalLineColor}`,
        flex: '2 0 0',
        [MEDIA_BREAKPOINTS.large]: {
          marginRight: `${largeVerticalGap * baseUnit}px`,
          marginLeft: `${largeVerticalGap * baseUnit}px`,
        },
        [MEDIA_BREAKPOINTS.medium]: {
          marginRight: `${mediumVerticalGap * baseUnit}px`,
          marginLeft: `${mediumVerticalGap * baseUnit}px`,
        },
        [MEDIA_BREAKPOINTS.small]: {
          marginRight: `${smallVerticalGap * baseUnit}px`,
          marginLeft: `${smallVerticalGap * baseUnit}px`,
        },
      },
    },
    description: {
      color: descriptionColor,
    },
  });
};

const createLinkTextStyles = () => StyleSheet.create({
  linkText: {
    [MEDIA_BREAKPOINTS.large]: {
      ':hover': {
        textDecoration: 'underline',
      },
    },
    [MEDIA_BREAKPOINTS.medium]: {
      ':hover': {
        color: 'initial',
      },
    },
    [MEDIA_BREAKPOINTS.small]: {
      ':hover': {
        color: 'initial',
      },
    },

  },
});

export interface LinkStyle {
  showArrow: boolean;
  arrowColor: string;
}

export interface StrikeThrowHorizontalLineStyle {
  strikeThrowHorizontalLineColor: string;
  showStrikeThrowHorizontalLine: boolean;
}

export interface MiddleHorizontalLineStyle {
  middleHorizontalLineColor: string;
  showMiddleHorizontalLine: boolean;
}

const createLink = ({ linkText,
  idleColor,
  activeStateColor,
  hoverStateColor,
  href,
  showArrow,
  arrowColor,
}: SectionHeaderDataProps & ThemedLinkStyle & LinkStyle) => {
  if (href && linkText) {
    const additionalStyle = {
      justifyContent: 'flex-end',
      marginLeft: 'auto',
    };
    const linkStyle = generateLinkStyleFromTheme({
      idleColor,
      activeStateColor,
      hoverStateColor,
    }, additionalStyle);
    const style = createLinkTextStyles();

    return (
      <TextLinkComponent href={href} linkStyle={linkStyle}>
        { linkText && showArrow
          ? <SeeAllArrowButton arrowColor={arrowColor} />
          : (
            <H4
              fontSizeLarge={TYPOGRAPHY_SIZE.BIGGER}
              fontSizeMedium={TYPOGRAPHY_SIZE.BIGGER}
              fontSizeSmall={TYPOGRAPHY_SIZE.BIGGER}
              styles={semanticCSS(style.linkText)}
            >
              {linkText}
            </H4>
          ) }
      </TextLinkComponent>
    );
  }
  return null;
};

export interface SectionHeaderComponentThemeProps {
  titleColor: string;
  titleBackgroundColor: string;
  borderTopColor: string;
  borderTopWidth: number;
  borderBottomColor: string;
  borderBottomWidth: number;
  squareColor: string;
  showSquare: boolean;
  showBorderTop: boolean;
  showBorderBottom: boolean;
  largeVerticalGap: number;
  mediumVerticalGap: number;
  smallVerticalGap: number;
  largeHorizontalGap: number;
  mediumHorizontalGap: number;
  smallHorizontalGap: number;
  largeWrapperGap: number;
  mediumWrapperGap: number;
  smallWrapperGap: number;
  showArrow: boolean;
  activeStateColor: string;
  hoverStateColor: string;
  arrowColor: string;
  showStrikeThrowHorizontalLine: boolean;
  strikeThrowHorizontalLineColor: string;
  showMiddleHorizontalLine: boolean;
  idleColor: string;
  middleHorizontalLineColor: string;
  descriptionColor: string;
  descriptionFontSizeLarge: TypographySize;
  descriptionFontSizeMedium: TypographySize;
  descriptionFontSizeSmall: TypographySize;
}

export interface SectionHeaderDataProps {
  linkText: string | null;
  href: string | null;
  description?: string;
}

export type SectionHeaderComponentProps = SectionHeaderDataProps & LayoutComponentBaseProps;

export const SectionHeader: React.FunctionComponent<SectionHeaderComponentProps> = props => {
  const themeProps = useTheme<SectionHeaderComponentThemeProps>(componentConfigurationKey);
  const { linkText, href, children, paddingFactors, description } = props;
  const {
    showSquare,
    activeStateColor,
    hoverStateColor,
    showArrow,
    arrowColor,
    showMiddleHorizontalLine,
    idleColor,
    descriptionFontSizeLarge,
    descriptionFontSizeMedium,
    descriptionFontSizeSmall } = themeProps;
  const style = createStyles(themeProps);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  return (
    <div className={css(paddingStyle, style.container)}>
      <div className={css(style.titleItem)}>
        {showSquare ? <div className={css(style.square)} /> : null }
        {children}
        {showMiddleHorizontalLine ? (<div className={css(style.middleHorizontalLineContainer)} />) : null}
        {createLink({
          linkText,
          idleColor,
          activeStateColor,
          hoverStateColor,
          href,
          showArrow,
          arrowColor,
        })}
      </div>
      {description ? <P styles={semanticCSS(style.description)} fontSizeSmall={descriptionFontSizeSmall} fontSizeMedium={descriptionFontSizeMedium} fontSizeLarge={descriptionFontSizeLarge}>{description}</P> : null}
    </div>
  );
};
